import type { FC } from "react";

/**
 * Individual event row component for provider progress display.
 */
import { getEventDisplayConfig } from "@arti/dashboard-events";

import type { EnrichedProviderProgressEvent } from "./eventDisplay";

import { formatRelativeTime } from "../../../utils/time";
import { ChevronRightIcon } from "../../common/icons";
import { EmbeddingProgressCard } from "./EmbeddingProgressCard";
import { getColorClassForEvent } from "./eventColors";
import { EventIcon } from "./EventIcon";
import { getEventSummary } from "./eventSummary";

const ERROR_EVENT_TYPES = new Set([
  "run:error",
  "tool:error",
  "tool:policy-violation",
  "phase:transition-blocked",
  "topology:failed",
]);

const MILESTONE_EVENT_TYPES = new Set([
  "completion:marked",
  "completion:verified",
  "run:start",
  "run:end",
]);

function resolveRowVariant(
  eventType: string,
): "error" | "milestone" | "default" {
  if (ERROR_EVENT_TYPES.has(eventType)) {
    return "error";
  }
  const config = getEventDisplayConfig(eventType);
  if (MILESTONE_EVENT_TYPES.has(eventType) || config?.notable) {
    return "milestone";
  }
  return "default";
}

const ROW_CLASSES = {
  default:
    "bg-white/60 hover:bg-slate-50 dark:bg-slate-900/30 dark:hover:bg-slate-800/50",
  error:
    "bg-red-50/60 border-l-2 border-l-red-400 hover:bg-red-50 dark:bg-red-950/20 dark:border-l-red-500/60 dark:hover:bg-red-950/30",
  milestone:
    "bg-slate-50/80 border-l-2 border-l-emerald-400 hover:bg-slate-100 dark:bg-slate-800/40 dark:border-l-emerald-500/60 dark:hover:bg-slate-800/60",
} as const;

interface ProviderEventRowProps {
  event: EnrichedProviderProgressEvent;
}

const StepSeparator: FC<{ stepNumber: number }> = ({ stepNumber }) => (
  <div className="flex items-center gap-2 px-3 pb-1 pt-3">
    <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
    <span className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
      Step {stepNumber}
    </span>
    <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
  </div>
);

export const ProviderEventRow: FC<ProviderEventRowProps> = ({ event }) => {
  if (
    event.event_type === "embedding:start" ||
    event.event_type === "embedding:progress" ||
    event.event_type === "embedding:complete"
  ) {
    return (
      <>
        {event.isFirstInStep && event.stepNumber !== null && (
          <StepSeparator stepNumber={event.stepNumber} />
        )}
        <EmbeddingProgressCard event={event} />
      </>
    );
  }

  const config = getEventDisplayConfig(event.event_type);
  const label = config?.label ?? event.event_type.replace(":", "::");
  const colorClass = getColorClassForEvent(event.event_type);
  const eventSummary = getEventSummary(event);
  const variant = resolveRowVariant(event.event_type);
  const rowClass = ROW_CLASSES[variant];

  return (
    <>
      {event.isFirstInStep && event.stepNumber !== null && (
        <StepSeparator stepNumber={event.stepNumber} />
      )}
      <div
        className={`group flex min-w-0 items-start gap-3 px-3 py-2.5 text-sm transition-colors ${rowClass}`}
      >
        {/* Timestamp */}
        <div className="mt-0.5 w-12 shrink-0 font-mono text-[10px] text-slate-400 dark:text-slate-500">
          {formatRelativeTime(event.received_at)}
        </div>

        {/* Icon */}
        <div
          className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-none ${colorClass}`}
        >
          <EventIcon type={event.event_type} />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-slate-700 dark:text-slate-200">
              {label}
            </span>
            {event.stepNumber !== null && !event.isFirstInStep && (
              <span className="font-mono text-[9px] text-slate-400 dark:text-slate-500">
                #{event.stepNumber}
              </span>
            )}
          </div>

          {eventSummary && (
            <p className="mt-0.5 truncate font-mono text-[10px] leading-relaxed text-slate-500 dark:text-slate-400">
              {eventSummary}
            </p>
          )}

          {Object.keys(event.event_data).length > 0 && (
            <details className="group/meta mt-1.5 overflow-hidden">
              <summary className="flex cursor-pointer select-none items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300">
                <ChevronRightIcon
                  size={10}
                  className="h-2.5 w-2.5 transition-transform group-open/meta:rotate-90"
                />
                Payload
              </summary>
              <pre className="custom-scrollbar mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap break-all border border-slate-200 bg-slate-50 p-2.5 font-mono text-[10px] leading-relaxed text-slate-600 shadow-inner dark:border-slate-700 dark:bg-arti-background dark:text-slate-400">
                {JSON.stringify(event.event_data, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </>
  );
};

export type { EnrichedProviderProgressEvent as EnrichedEvent };
